import { AJUDA_FINANCEIRO } from "@/lib/ajuda/dialogos/financeiro";
import { AlertTriangle, Loader2, Lock } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@bookdental/ui";
import { nomeDoMes } from "@bookdental/schemas";
import { trpc } from "@/lib/trpc";
import { reais } from "@/lib/dinheiro";

/** Confere o mês antes de fechar: o resumo que vira oficial e o que ainda está pendente. */
export function FecharMesDialog({ competencia, onFechar }: { competencia: string | null; onFechar: () => void }) {
  const utils = trpc.useUtils();
  const previa = trpc.financeiro.fechamento.previa.useQuery({ competencia: competencia ?? "2026-01" }, { enabled: !!competencia }).data;
  const fechar = trpc.financeiro.fechamento.fechar.useMutation({
    onSuccess: () => {
      toast.success(`${competencia ? nomeDoMes(competencia).replace(/^./, (c) => c.toUpperCase()) : "Mês"} fechado.`);
      utils.financeiro.invalidate();
      onFechar();
    },
    onError: (e) => toast.error(e.message),
  });
  const r = previa?.resumo;
  const linhas = r
    ? [
        { rotulo: "Receita", valor: r.receitaCents },
        { rotulo: "Custo variável", valor: -r.custoVariavelCents },
        { rotulo: "Custos fixos", valor: -r.custosFixosCents },
        { rotulo: "Resultado", valor: r.resultadoCents, forte: true },
      ]
    : [];

  return (
    <Dialog open={!!competencia} onOpenChange={(v) => !v && onFechar()}>
      <DialogContent className="max-w-md border border-zinc-200 bg-white shadow-xl dark:border-border dark:bg-card">
        <DialogHeader>
          <DialogTitle ajuda={AJUDA_FINANCEIRO.fecharMes}>Fechar {competencia ? nomeDoMes(competencia) : ""}</DialogTitle>
          <DialogDescription>
            Estes números ficam como o resultado oficial do mês. Depois de fechado, nada com competência nele muda: correção entra como estorno num mês aberto.
          </DialogDescription>
        </DialogHeader>
        {!previa ? (
          <p className="py-6 text-center text-sm text-zinc-500">Carregando…</p>
        ) : (
          <>
            <div className="space-y-1 rounded-lg border border-zinc-200 p-3 text-sm dark:border-border">
              {linhas.map((l) => (
                <p key={l.rotulo} className={`flex justify-between ${l.forte ? "border-t border-zinc-100 pt-1 font-semibold dark:border-border" : ""}`}>
                  <span className="text-zinc-600 dark:text-muted-foreground">{l.rotulo}</span>
                  <span className={`tabular-nums ${l.forte ? (l.valor >= 0 ? "text-emerald-600" : "text-red-600") : "text-zinc-900 dark:text-foreground"}`}>{reais(l.valor)}</span>
                </p>
              ))}
            </div>
            {(previa.semPreco > 0 || previa.contasAbertas.quantidade > 0) && (
              <ul className="space-y-1 rounded-lg bg-amber-50 p-3 text-xs text-amber-800 dark:bg-amber-500/10 dark:text-amber-300">
                {previa.semPreco > 0 && (
                  <li className="flex gap-1.5">
                    <AlertTriangle className="h-3.5 w-3.5 shrink-0" />
                    {previa.semPreco} {previa.semPreco === 1 ? "serviço concluído sem preço" : "serviços concluídos sem preço"}: ficam fora da receita.
                  </li>
                )}
                {previa.contasAbertas.quantidade > 0 && (
                  <li className="flex gap-1.5">
                    <AlertTriangle className="h-3.5 w-3.5 shrink-0" />
                    {previa.contasAbertas.quantidade} {previa.contasAbertas.quantidade === 1 ? "conta do mês ainda não paga" : "contas do mês ainda não pagas"} ({reais(previa.contasAbertas.valorCents)}). Dá para pagar depois de fechar.
                  </li>
                )}
              </ul>
            )}
          </>
        )}
        <div className="flex justify-end gap-2">
          <button type="button" onClick={onFechar} className="h-9 rounded-md px-4 text-sm text-zinc-600 hover:bg-zinc-100 dark:text-muted-foreground dark:hover:bg-muted">
            Cancelar
          </button>
          <button
            type="button"
            disabled={!previa || fechar.isPending}
            onClick={() => competencia && fechar.mutate({ competencia })}
            className="inline-flex h-9 items-center gap-2 rounded-md bg-brand-600 px-4 text-sm font-semibold text-white shadow-sm hover:bg-brand-700 disabled:opacity-50"
          >
            {fechar.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Lock className="h-4 w-4" />}
            Fechar o mês
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
